/**
 * DAC profiles -- group 11 (European high-end converters and digital sources).
 *
 * The brands collected here are, with two exceptions, amplifier and source
 * houses rather than chipset integrators, and most of them publish topology
 * descriptions rather than full electrical specifications. That shapes the
 * provenance of this file: where a figure is genuinely published it is tagged
 * MANUFACTURER_SPEC, and where it had to be inferred from the stated topology
 * the entry carries ESTIMATED and says so explicitly. Converter part numbers
 * are named only when the manufacturer names them; otherwise the `core` string
 * describes the architecture class and stops there. Nothing here is a
 * measurement of an individual unit.
 */

import type { DacProfile } from '../../types/hardware';
import {
  AKM_FILTERS,
  FILTERS,
  HARMONIC_SHAPES,
  SIMPLE_FILTERS,
  TUBE_LINE,
  makeDac,
  usd,
} from '../kit';

export const DACS_11: DacProfile[] = [
  makeDac({
    mfr: 'Ideon Audio',
    model: 'Ayazi mk2',
    price: usd(
      4900,
      'APPROXIMATE',
      2023,
      'Approximate US retail. Ideon sells through regional distribution and does not publish a single global list price, so this figure is not exact.',
    ),
    year: 2019,
    country: 'Greece',
    architecture: 'delta-sigma',
    core: 'Delta-sigma converter core feeding a discrete Class A analogue stage; converter part number is not published by the manufacturer',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 118,
    thdPlusNDb: -104,
    crosstalkDb: -112,
    outputVrmsSE: 2.2,
    outputImpedanceOhms: 100,
    balanced: false,
    dsd: 'dsd256',
    filters: [FILTERS.sharpLinear(128)],
    jitterPsRms: 30,
    noiseShaperOrder: 5,
    slewRateVus: 18,
    confidence: 'ESTIMATED',
    accent: '#4fa3a8',
    tags: ['greece', 'single-ended', 'discrete-output', 'fixed-filter'],
    notes:
      'Single-ended-only converter with a discrete Class A output stage and no user-selectable reconstruction filter, so this profile exposes exactly one impulse response instead of a filter menu. The 100 ohm source impedance is high enough to interact audibly with a short-input-impedance line stage, and the discrete stage leaves a second-harmonic-weighted residue rather than the odd-order pattern an integrated op-amp I/V section produces. Dynamic range sits roughly 10 dB below current chipset best practice because the discrete stage trades noise floor for transfer-curve smoothness.',
    provenanceNote:
      'Topology (single-ended discrete Class A output, no filter selection) follows Ideon published product description. Dynamic range, THD+N, channel separation, output impedance, output level and jitter are ESTIMATED from that topology and from comparable discrete-output designs; Ideon does not publish a full electrical specification for this model. The converter part number is not published and is deliberately not named here. Price is approximate.',
  }),

  makeDac({
    mfr: 'Innuos',
    model: 'ZENmini MK3',
    price: usd(
      1499,
      'APPROXIMATE',
      2023,
      'Approximate US retail for a 1 TB configuration fitted with the internal DAC option. Innuos pricing varies with storage capacity and market, so this is not an exact list figure.',
    ),
    year: 2019,
    country: 'Portugal',
    architecture: 'delta-sigma',
    core: 'Server-board delta-sigma converter with integrated line output; part number is not published',
    outputStage: 'op-amp',
    dynamicRangeDb: 110,
    thdPlusNDb: -98,
    crosstalkDb: -100,
    outputVrmsSE: 2.1,
    outputImpedanceOhms: 120,
    balanced: false,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'dsd64',
    filters: SIMPLE_FILTERS(),
    jitterPsRms: 110,
    noiseShaperOrder: 4,
    effectiveBits: 18,
    confidence: 'ESTIMATED',
    accent: '#7d8ba1',
    tags: ['portugal', 'server-integrated', 'convenience-dac', 'rate-limited'],
    notes:
      'This is the converter section of a music server, not a standalone DAC, and the profile is built around that fact: the analogue stage shares a chassis and a supply with a CPU, an SSD and an Ethernet PHY, so the modelled noise floor and jitter are set by the digital environment rather than by the converter itself. The 192 kHz / 24-bit ceiling and DSD64 limit are real functional constraints, and the two-option filter menu is the stock chipset roll-off pair rather than an authored filter design. Compared with the dedicated converters in this group it trades roughly 10 dB of dynamic range and a factor of four in jitter for integration.',
    provenanceNote:
      'Rate and bit-depth limits and the existence of the internal converter option follow Innuos published product documentation. Dynamic range, THD+N, channel separation, output impedance and jitter are ESTIMATED, since Innuos does not publish an electrical specification for the internal DAC and does not name its part number. Price is approximate and configuration-dependent.',
  }),

  makeDac({
    mfr: 'Pilium Audio',
    model: 'Hermes',
    price: usd(
      24000,
      'APPROXIMATE',
      2024,
      'Approximate. Neither the model designation nor its price could be confirmed against published Pilium Audio documentation; treat this entry as provisional.',
    ),
    year: 2022,
    country: 'Greece',
    architecture: 'fpga-custom',
    core: 'FPGA-managed conversion and reclocking feeding a fully discrete dual-mono analogue stage; implementation details are not published',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 124,
    thdPlusNDb: -112,
    crosstalkDb: -125,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 15,
    balanced: true,
    dsd: 'dsd512',
    filters: [FILTERS.apodizing(128), FILTERS.sharpLinear(128), FILTERS.slowMinimum(32)],
    defaultFilterId: 'apodizing',
    jitterPsRms: 15,
    internalOversampling: 16,
    slewRateVus: 45,
    confidence: 'ESTIMATED',
    accent: '#9b7bd4',
    tags: ['greece', 'fpga', 'dual-mono', 'provisional-entry'],
    notes:
      'Modelled as an FPGA-managed converter with reclocking ahead of the modulator and a fully discrete dual-mono output stage, which is the design language Pilium uses in its amplifiers. The distinguishing DSP behaviour is the default apodizing filter: it removes pre-ringing at the cost of a slightly earlier band-edge roll-off, so transient reproduction differs from the sharp linear-phase alternative even though the steady-state magnitude response is nearly identical. The very low 15 ohm output impedance and high slew figure mean it drives cable capacitance and a following input stage without the HF loss the higher-impedance entries in this group show.',
    provenanceNote:
      'WARNING: this entry is provisional. The model designation could not be verified against published Pilium Audio material, and no manufacturer specification was available for it. Architecture, dynamic range, THD+N, channel separation, output level, output impedance, jitter and price are ALL ESTIMATED from the design language of the Pilium amplifier range and from comparable dual-mono discrete converters. Nothing in this entry should be treated as a published figure until the model is confirmed.',
  }),

  makeDac({
    mfr: 'Primare',
    model: 'DAC30',
    price: usd(
      3000,
      'APPROXIMATE',
      2023,
      'Approximate US retail; Primare list pricing varies noticeably between markets.',
    ),
    year: 2019,
    country: 'Sweden',
    architecture: 'multibit-delta-sigma',
    core: 'AKM Velvet Sound switched-resistor multibit converter with a two-stage op-amp I/V and buffer',
    outputStage: 'op-amp',
    dynamicRangeDb: 122,
    thdPlusNDb: -110,
    crosstalkDb: -120,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'dsd256',
    filters: AKM_FILTERS(),
    defaultFilterId: 'sharp-linear',
    jitterPsRms: 35,
    noiseShaperOrder: 6,
    confidence: 'ESTIMATED',
    accent: '#5b6f8c',
    tags: ['sweden', 'akm', 'switched-resistor', 'filter-menu'],
    notes:
      'A switched-resistor multibit converter behaves differently from a single-bit modulator at low signal levels: the element array carries the signal directly rather than through a high-order noise shaper, so the idle-tone tendency is lower and the distortion residue does not fall away as steeply below -60 dBFS. The six-option AKM filter menu is the widest in this group and includes the super-slow roll-off setting, which is aliasing-tolerant by design and is the only filter here that lets audible-band images through. Output is a conventional op-amp stage, so the harmonic residue is odd-weighted and level-independent.',
    provenanceNote:
      'Architecture class (AKM switched-resistor Velvet Sound), balanced output, DSD support and the filter menu follow Primare published product documentation. The exact converter part number is not confirmed here and is described only by family. Dynamic range and THD+N are ESTIMATED at the level this converter family and analogue implementation reach; Primare does not publish a full measured specification. Price is approximate.',
  }),

  makeDac({
    mfr: 'Copland',
    model: 'DAC215',
    price: usd(
      3500,
      'APPROXIMATE',
      2023,
      'Approximate US retail; the model has been sold at a range of prices across markets and revisions.',
    ),
    year: 2020,
    country: 'Denmark',
    architecture: 'delta-sigma',
    core: 'Delta-sigma converter with a twin-triode analogue output stage and passive volume control',
    outputStage: 'tube',
    dynamicRangeDb: 106,
    thdPlusNDb: -84,
    crosstalkDb: -86,
    outputVrmsSE: 2.3,
    outputImpedanceOhms: 700,
    balanced: false,
    dsd: 'dsd256',
    filters: [FILTERS.slowMinimum(32), FILTERS.sharpLinear(128)],
    defaultFilterId: 'slow-min',
    jitterPsRms: 55,
    noiseShaperOrder: 5,
    effectiveBits: 17,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(7, -0.9),
    slewRateVus: 14,
    confidence: 'ESTIMATED',
    accent: '#d9a05b',
    tags: ['denmark', 'tube-output', 'headphone-output', 'minimum-phase-default'],
    notes:
      'A modern delta-sigma front end followed by a cathode-follower triode output stage, which is the whole point of the profile: the conversion is clean and the colouration is added deliberately downstream. The transfer curve is strongly asymmetric, so second harmonic dominates and grows about 1 dB per dB of drive rather than sitting at a fixed floor, and the 700 ohm output impedance combines with cable and input capacitance to produce a measurable top-octave loss into anything under about 20 kohm. The default slow minimum-phase filter removes pre-ringing entirely but passes more out-of-band image energy than the sharp alternative.',
    provenanceNote:
      'Tube output topology, single-ended output and the filter options follow Copland published product description. Dynamic range, THD+N, channel separation, output impedance and jitter are ESTIMATED from that topology; like most valve-output manufacturers Copland does not publish a THD figure for this model. Price is approximate.',
  }),

  makeDac({
    mfr: 'Norma Audio',
    model: 'HS-DA1 PRE',
    price: usd(
      6500,
      'APPROXIMATE',
      2023,
      'Approximate US retail for the DAC/preamplifier configuration; option content changes the price materially.',
    ),
    year: 2014,
    country: 'Italy',
    architecture: 'multibit-delta-sigma',
    core: 'Advanced-segment multibit converter in dual-mono configuration; part number is not published',
    outputStage: 'jfet',
    dynamicRangeDb: 116,
    thdPlusNDb: -102,
    crosstalkDb: -106,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 30,
    balanced: true,
    dsd: 'dsd128',
    filters: SIMPLE_FILTERS(),
    defaultFilterId: 'sharp-linear',
    jitterPsRms: 50,
    noiseShaperOrder: 5,
    slewRateVus: 60,
    confidence: 'ESTIMATED',
    accent: '#8c5b6f',
    tags: ['italy', 'dual-mono', 'wide-bandwidth', 'jfet-output'],
    notes:
      'Norma builds very wide bandwidth, low global feedback analogue stages, and this profile carries that signature into the converter: a JFET output stage with a square-law transfer characteristic, so the distortion residue is second-harmonic dominant and decays quickly with order, unlike the odd-order tail a high-feedback op-amp leaves. The 60 V/us slew figure and 30 ohm source impedance make it insensitive to interconnect load, which is the practical difference between this and the tube and discrete Class A entries here. Dual-mono conversion is modelled as improved channel separation rather than as any change to the transfer curve.',
    provenanceNote:
      'Dual-mono layout, balanced output and preamplifier function follow Norma Audio published product description. The converter part number is not published, so the architecture class here is an INFERENCE and the entry is tagged ESTIMATED accordingly; dynamic range, THD+N, output impedance, slew rate and jitter are likewise estimates from the stated wide-bandwidth discrete topology. Price is approximate and option-dependent.',
  }),

  makeDac({
    mfr: 'Audia Flight',
    model: 'FL DAC',
    price: usd(
      3500,
      'APPROXIMATE',
      2023,
      'Approximate. The exact model designation and its list price could not be confirmed against published Audia Flight documentation.',
    ),
    year: 2018,
    country: 'Italy',
    architecture: 'delta-sigma',
    core: 'Dual differential delta-sigma converters feeding a discrete current-feedback analogue stage',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 119,
    thdPlusNDb: -106,
    crosstalkDb: -114,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 22,
    balanced: true,
    dsd: 'dsd256',
    filters: [FILTERS.sharpLinear(128), FILTERS.slowLinear(48), FILTERS.apodizing(128)],
    defaultFilterId: 'sharp-linear',
    jitterPsRms: 40,
    noiseShaperOrder: 6,
    slewRateVus: 90,
    confidence: 'ESTIMATED',
    accent: '#c46a4f',
    tags: ['italy', 'current-feedback', 'dual-differential', 'provisional-entry'],
    notes:
      'Modelled on the current-feedback discrete analogue stage Audia Flight uses throughout its amplifier range: current-feedback topologies hold their loop gain essentially constant with frequency, so the distortion residue does not rise through the top two octaves the way a voltage-feedback stage running out of loop gain does. That is represented here as a Class A harmonic distribution with a very high 90 V/us slew figure and a low 22 ohm source impedance. Running the converters differentially cancels even-order products from the conversion itself, leaving the analogue stage as the dominant contributor.',
    provenanceNote:
      'WARNING: the model designation for this entry could not be confirmed against published Audia Flight material, and no manufacturer electrical specification was located. The current-feedback discrete analogue topology follows the documented Audia Flight house design used across the amplifier range, but dynamic range, THD+N, channel separation, output level, output impedance, slew rate, jitter and price are ALL ESTIMATED. Treat this entry as provisional.',
  }),

  makeDac({
    mfr: 'Goldmund',
    model: 'Mimesis 20',
    price: usd(
      16000,
      'APPROXIMATE',
      1993,
      'Approximate original retail. Long discontinued, and Goldmund did not publish a stable US list price for it, so this figure is an estimate of period pricing rather than a quoted MSRP.',
    ),
    year: 1991,
    country: 'Switzerland',
    architecture: 'ladder-multibit',
    core: '20-bit hybrid ladder converter modules, one per channel, fed by a digital interpolation filter',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 104,
    thdPlusNDb: -90,
    crosstalkDb: -98,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 5,
    balanced: true,
    sampleRates: [32000, 44100, 48000],
    maxBitDepth: 20,
    dsd: 'none',
    filters: [FILTERS.sharpLinear(256), FILTERS.nosCompensated()],
    defaultFilterId: 'sharp-linear',
    ladderMismatchLsb: 0.7,
    effectiveBits: 17,
    jitterPsRms: 160,
    internalOversampling: 8,
    slewRateVus: 200,
    confidence: 'ESTIMATED',
    accent: '#b8b0a0',
    tags: ['switzerland', 'ladder', 'early-digital', 'discontinued'],
    notes:
      'A period ladder converter, and the profile is deliberately built to show what that costs and what it buys. Element mismatch of roughly 0.7 LSB produces level-dependent integral nonlinearity, so distortion does not collapse toward the noise floor at low signal level the way a noise-shaped converter does -- it stays broadly constant as a ratio and its spectrum decays slowly with harmonic order. Against that, the discrete Class A output runs at a 5 ohm source impedance and a 200 V/us slew rate, and the input is limited to 20-bit, 48 kHz S/PDIF with a clock roughly an order of magnitude noisier than anything modern in this group.',
    provenanceNote:
      'Architecture class (multibit ladder modules, one per channel), rate and bit-depth limits and the discrete Class A output follow period Goldmund published material. The ladder module supplier and part number are NOT confirmed here and are deliberately not named. Dynamic range, THD+N, ladder mismatch in LSB, effective bits, jitter, output impedance and slew rate are all ESTIMATED from the period and the stated topology, not measured. Price is an approximate reconstruction of original retail.',
  }),
];
